import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/agriculture')({
  component: AgriculturePage,
  head: () => ({
    meta: [
      { title: 'Agriculture services — Warren-Blackwood Electorate Guide' },
      {
        name: 'description',
        content:
          'Farming services for Warren-Blackwood: DPIRD local offices, biosecurity, pest & weed reporting, livestock brands and movement, farm water, grains hotline and Rural Financial Counselling.',
      },
      { property: 'og:title', content: 'Agriculture services — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'DPIRD contacts, biosecurity, pests & weeds, livestock movement, farm water and Rural Financial Counselling for south-west farming families.',
      },
    ],
  }),
});

function AgriculturePage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.5rem',
    color: 'var(--rivergum)',
    marginTop: 'var(--sp-xl)',
    marginBottom: 'var(--sp-sm)',
    borderBottom: '1px solid var(--light-border)',
    paddingBottom: '0.3rem',
    letterSpacing: '0.02em',
  };
  const h3: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.1rem',
    color: 'var(--ink)',
    marginTop: 'var(--sp-md)',
    marginBottom: '0.4rem',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.65,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };
  const callout = (bg: string, border: string): React.CSSProperties => ({
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 8,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  });

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Services · Agriculture
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Agriculture services
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-lg)',
          lineHeight: 1.5,
        }}
      >
        Bread-and-butter contacts for farming families across Warren-Blackwood
        — DPIRD offices, biosecurity, pest and weed reporting, livestock
        brands and movement, farm water and the Rural Financial Counselling
        Service. Save this page to your phone.
      </p>

      <div style={callout('#eef5fb', '#b9d4e8')}>
        <strong style={{ color: '#1b4f72' }}>Numbers worth memorising</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          DPIRD Pest &amp; Disease Information Service — <a href="tel:0893683080" style={link}>(08) 9368 3080</a> (free identification &amp; advice)
          <br />
          Emergency Animal Disease hotline — <a href="tel:1800675888" style={link}>1800 675 888</a> (24/7)
          <br />
          Exotic Plant Pest Hotline — <a href="tel:1800084881" style={link}>1800 084 881</a>
          <br />
          WA Grains Industry hotline — <a href="tel:1800198231" style={link}>1800 198 231</a>
          <br />
          Rural Financial Counselling Service WA — <a href="tel:1800612004" style={link}>1800 612 004</a> (free, confidential)
        </p>
      </div>

      <h2 style={h2}>DPIRD — Department of Primary Industries &amp; Regional Development</h2>
      <p style={p}>
        DPIRD is the WA Government's primary department for farmers,
        pastoralists, foresters and fishers. They run agronomy advice,
        biosecurity programs, animal welfare, livestock identification,
        research and grants.
      </p>
      <h3 style={h3}>Local offices serving Warren-Blackwood</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>DPIRD Manjimup</strong> — the main south-west horticulture
          research and advisory office. Address and current contacts on{' '}
          <a href="https://www.dpird.wa.gov.au/" style={link}>
            dpird.wa.gov.au
          </a>
          .
        </li>
        <li>
          <strong>DPIRD Bunbury</strong> — broader south-west regional
          office; livestock, dairy and broadacre advice.
        </li>
        <li>
          <strong>DPIRD Albany</strong> — Great Southern regional office;
          serves Denmark and the southern end of the electorate.
        </li>
        <li>
          DPIRD switchboard — <a href="tel:1300374731" style={link}>1300 374 731</a> · {' '}
          <a href="mailto:enquiries@dpird.wa.gov.au" style={link}>enquiries@dpird.wa.gov.au</a>
        </li>
      </ul>
      <p style={p}>
        <a href="https://www.dpird.wa.gov.au/" style={link}>dpird.wa.gov.au — DPIRD home →</a>
      </p>

      <h2 style={h2}>Biosecurity</h2>
      <p style={p}>
        Biosecurity is everyone's job. WA's enviable freedom from many
        livestock and plant pests depends on quick reporting. <strong>If in
        doubt, report it</strong> — assessment is free and confidential, and
        you won't be penalised for a false alarm.
      </p>
      <h3 style={h3}>Animal disease</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Emergency Animal Disease hotline</strong> —{' '}
          <a href="tel:1800675888" style={link}>1800 675 888</a> — 24/7
          national hotline. Use immediately for unusual deaths, foot-and-mouth
          symptoms, lumpy skin disease signs or any unusual disease event.
        </li>
        <li>
          For non-urgent animal health concerns, contact your local
          veterinarian or DPIRD field veterinary officer through the Pest
          &amp; Disease Information Service.
        </li>
      </ul>
      <h3 style={h3}>Plant pests &amp; diseases</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Exotic Plant Pest Hotline</strong> —{' '}
          <a href="tel:1800084881" style={link}>1800 084 881</a> — report
          unusual pests, fungi or symptoms on crops, vines, orchards or
          ornamentals.
        </li>
        <li>
          <strong>MyPestGuide Reporter app</strong> — photograph and submit
          unknown pests; DPIRD identifies and responds.{' '}
          <a href="https://www.dpird.wa.gov.au/" style={link}>
            dpird.wa.gov.au — MyPestGuide →
          </a>
        </li>
      </ul>

      <h2 style={h2}>Declared pests &amp; weeds</h2>
      <p style={p}>
        Under the WA <em>Biosecurity and Agriculture Management Act 2007</em>,
        landholders have legal obligations to control{' '}
        <strong>declared pests</strong> on their property. Common declared
        pests/weeds in the south-west include European wasp, blackberry,
        cotton bush, arum lily, bridal creeper and feral pigs.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          Search the official declared species list —{' '}
          <a href="https://www.dpird.wa.gov.au/" style={link}>
            WA Organism List (WAOL) →
          </a>
        </li>
        <li>
          <strong>Report a declared pest or weed</strong> — Pest &amp; Disease
          Information Service on <a href="tel:0893683080" style={link}>(08) 9368 3080</a>{' '}
          or through MyPestGuide Reporter.
        </li>
        <li>
          <strong>Recognised Biosecurity Groups (RBGs)</strong> coordinate
          local landholder action on declared pests. Warren-Blackwood is
          served by the <a href="https://www.swbg.org.au/" style={link}>South West Biosecurity Group</a>{' '}
          and, in the Great Southern, <a href="https://www.gsbg.org.au/" style={link}>Great Southern Biosecurity Group</a>.
        </li>
      </ul>

      <h2 style={h2}>Livestock — brands, NLIS &amp; movement</h2>
      <p style={p}>
        WA's livestock identification system underpins biosecurity, market
        access and trace-back. If you own livestock you almost certainly
        need to be registered.
      </p>
      <h3 style={h3}>Property Identification Code (PIC) &amp; brands</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          Anyone with one or more head of cattle, sheep, goats, pigs,
          camelids or commercial poultry needs a <strong>PIC</strong> for the
          property where they're kept. Apply free through DPIRD.
        </li>
        <li>
          A <strong>registered stock brand or earmark</strong> is required
          to identify cattle, sheep or goats sold or moved off the property.
        </li>
        <li>
          Apply, transfer or renew via{' '}
          <a href="https://www.dpird.wa.gov.au/" style={link}>
            dpird.wa.gov.au — livestock identification →
          </a>
        </li>
      </ul>
      <h3 style={h3}>NLIS &amp; movement (NVD/eNVD)</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          All cattle, sheep and goats moved between properties must travel
          with a <strong>National Vendor Declaration (NVD)</strong> or
          electronic <strong>eNVD</strong>.
        </li>
        <li>
          NLIS tags must be applied before stock leave a property of birth.
        </li>
        <li>
          NLIS WA support — <a href="tel:1800013100" style={link}>1800 013 100</a> ·{' '}
          <a href="https://www.nlis.com.au/" style={link}>nlis.com.au →</a>
        </li>
      </ul>

      <h2 style={h2}>Farm water, licensing &amp; catchment</h2>
      <p style={p}>
        Reliable farm water is a perennial issue in Warren-Blackwood —
        drying springs, salinity creep, irrigation entitlements and bore
        approvals all sit across multiple agencies. The starting points:
      </p>

      <h3 style={h3}>Water licensing &amp; bore approvals (DWER)</h3>
      <p style={p}>
        The <strong>Department of Water and Environmental Regulation
        (DWER)</strong> licences water take from rivers, dams, springs and
        groundwater bores under the <em>Rights in Water and Irrigation Act
        1914</em>. Most commercial agricultural use needs a licence; stock
        and domestic use from your own land is usually licence-free but
        capped.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Water licence application (5C)</strong> — needed to take
          surface or groundwater above stock-and-domestic limits.{' '}
          <a href="https://www.wa.gov.au/service/environment/water-management/apply-water-licence" style={link}>
            wa.gov.au — apply for a water licence →
          </a>
        </li>
        <li>
          <strong>Permit to construct or alter a bore (26D)</strong> — most
          new bores in proclaimed groundwater areas need a permit, and a
          licensed driller is required.{' '}
          <a href="https://www.wa.gov.au/service/environment/water-management/apply-permit-construct-or-alter-bore" style={link}>
            wa.gov.au — bore construction permit →
          </a>
        </li>
        <li>
          <strong>Dam approval</strong> — new or expanded farm dams above
          certain thresholds need DWER approval, especially in proclaimed
          surface water areas (covers much of the Blackwood, Warren and
          Donnelly catchments).
        </li>
        <li>
          <strong>DWER Bunbury (South West)</strong> &amp; <strong>DWER
          Albany (South Coast)</strong> are the relevant regional offices for
          Warren-Blackwood. Contact details —{' '}
          <a href="https://www.wa.gov.au/organisation/department-of-water-and-environmental-regulation" style={link}>
            Department of Water and Environmental Regulation →
          </a>
        </li>
      </ul>
      <p style={p}>
        <a href="https://www.wa.gov.au/organisation/department-of-water-and-environmental-regulation" style={link}>
          dwer.wa.gov.au — full water licensing portal →
        </a>
      </p>

      <h3 style={h3}>Blackwood, Warren &amp; Donnelly catchment management</h3>
      <p style={p}>
        The Blackwood is one of WA's largest river systems and has
        long-running salinity, eutrophication and flow-management challenges
        — most pronounced in the upper Blackwood (Boyup Brook eastward) and
        easing as it nears Augusta. The Warren and Donnelly catchments
        around Manjimup, Pemberton and Northcliffe carry their own pressures
        around forestry transition, horticultural drawdown and water
        quality.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Blackwood Basin Group</strong> — community-led NRM body for
          the entire Blackwood catchment; on-ground projects, monitoring,
          revegetation and landholder support.{' '}
          <a href="https://www.blackwoodbasingroup.com.au/" style={link}>blackwoodbasingroup.com.au →</a>
        </li>
        <li>
          <strong>South West Catchments Council (SWCC)</strong> — the
          regional NRM organisation; coordinates funding for revegetation,
          waterway restoration, fencing and water-quality monitoring across
          the south-west.{' '}
          <a href="https://www.swccnrm.org.au/" style={link}>swccnrm.org.au →</a>
        </li>
        <li>
          <strong>DWER Water Information Reporting</strong> — historical
          streamflow, rainfall and groundwater data for the Blackwood,
          Warren and Donnelly systems.{' '}
          <a href="https://wir.water.wa.gov.au/" style={link}>wir.water.wa.gov.au →</a>
        </li>
        <li>
          <strong>Blackwood and Warren Surface Water Allocation Plans</strong>{' '}
          — the statutory framework that sets sustainable diversion limits
          and informs licensing decisions in each catchment. Worth reading
          before applying for a new entitlement in a fully-allocated reach.
        </li>
      </ul>

      <h3 style={h3}>Salinity management</h3>
      <p style={p}>
        Dryland salinity remains a significant issue in the upper Blackwood
        and parts of the Boyup Brook and Bridgetown-Greenbushes shires.
        Support is available through:
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>DPIRD soil and land management</strong> — practical
          advice on salinity, soil acidity, waterlogging and revegetation
          options.{' '}
          <a href="https://www.dpird.wa.gov.au/" style={link}>
            dpird.wa.gov.au — soil constraints →
          </a>
        </li>
        <li>
          <strong>SWCC and Blackwood Basin Group</strong> deliver on-ground
          works funding (fencing, revegetation, perennial pasture trials)
          targeted at salinity-affected land.
        </li>
        <li>
          <strong>DPIRD eConnected Grainbelt &amp; soil mapping tools</strong>{' '}
          — public maps of salinity risk, soil types and groundwater
          conditions.{' '}
          <a href="https://www.dpird.wa.gov.au/" style={link}>
            dpird.wa.gov.au — digital agriculture →
          </a>
        </li>
      </ul>

      <h3 style={h3}>Irrigation scheme access</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Southern Forests Irrigation Scheme (Manjimup)</strong> —
          long-discussed regional irrigation expansion to improve water
          security for the Southern Forests horticulture industry. Status
          and current scope sit with DPIRD and the Southern Forests Food
          Council.
        </li>
        <li>
          <strong>Existing private and co-operative schemes</strong> —
          several small irrigator co-operatives operate around Manjimup and
          Pemberton. Membership and trade arrangements vary; SWCC and DPIRD
          can point you to the right contact.
        </li>
        <li>
          <strong>Water trading</strong> — entitlement trade within fully
          allocated systems is administered by DWER.
        </li>
      </ul>

      <h3 style={h3}>Water infrastructure programs &amp; rebates</h3>
      <p style={p}>
        Funding for on-farm and regional water infrastructure is delivered
        across several state programs. The big ones:
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Farm Water Rebate Scheme (DPIRD)</strong> — rebate for
          eligible on-farm water infrastructure (tanks, pipes, desilting
          dams). Round-based.{' '}
          <a href="https://www.dpird.wa.gov.au/" style={link}>
            dpird.wa.gov.au — Farm Water Rebate →
          </a>
        </li>
        <li>
          <strong>Emergency Water Deficiency Program</strong> — for
          declared local government areas during severe deficiency events,
          administered by DWER with shire delivery.{' '}
          <a href="https://www.wa.gov.au/service/environment/water-management/emergency-water-deficiency-program" style={link}>
            wa.gov.au — Emergency Water Deficiency →
          </a>
        </li>
        <li>
          <strong>South West Development Commission</strong> and{' '}
          <strong>Great Southern Development Commission</strong> — the
          regional development commissions co-fund regional water and
          drainage projects through the Royalties for Regions program.
          (Note: there is no separate "Department of Regional Development
          WA" — regional development now sits within DPIRD, delivered
          through the regional commissions.){' '}
          <a href="https://www.swdc.wa.gov.au/" style={link}>swdc.wa.gov.au →</a>{' '}
          ·{' '}
          <a href="https://gsdc.wa.gov.au/" style={link}>gsdc.wa.gov.au →</a>
        </li>
        <li>
          <strong>National Water Grid Fund</strong> — federal co-funding
          for regional water infrastructure that improves agricultural
          productivity and drought resilience.{' '}
          <a href="https://www.nationalwatergrid.gov.au/" style={link}>nationalwatergrid.gov.au →</a>
        </li>
      </ul>

      <h3 style={h3}>Other water contacts</h3>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Water Corporation regional faults</strong> —{' '}
          <a href="tel:131375" style={link}>13 13 75</a> for scheme-water
          issues (where connected).
        </li>
        <li>
          <strong>Drillers Licensing (DWER)</strong> — only a licensed
          driller can construct bores in proclaimed areas; check current
          register on the DWER site before booking.
        </li>
      </ul>



      <h2 style={h2}>Grains industry</h2>
      <p style={p}>
        The southern end of the electorate sits on the edge of WA's grain
        belt. Bridgetown, Boyup Brook and Manjimup all have grain growers
        delivering through CBH.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>WA Grains Industry hotline</strong> —{' '}
          <a href="tel:1800198231" style={link}>1800 198 231</a> — grower
          information and support.
        </li>
        <li>
          <strong>CBH Group</strong> — bin sites, receival standards and
          grower services. <a href="https://www.cbh.com.au/" style={link}>cbh.com.au →</a>
        </li>
        <li>
          <strong>Grain Industry Association of WA (GIWA)</strong> — industry
          intelligence, crop reports.{' '}
          <a href="https://www.giwa.org.au/" style={link}>giwa.org.au →</a>
        </li>
        <li>
          <strong>WAFarmers</strong> — peak grower body for advocacy,
          contracts and policy.{' '}
          <a href="https://www.wafarmers.org.au/" style={link}>wafarmers.org.au →</a>
        </li>
      </ul>

      <h2 style={h2}>Rural Financial Counselling Service WA</h2>
      <p style={p}>
        Free, confidential and independent financial counselling for primary
        producers, fishers, foresters and small related businesses in
        financial difficulty. Counsellors travel to the farm — no kitchen
        table is too messy, and they've seen worse.
      </p>
      <p style={p}>
        <a href="tel:1800612004" style={link}>1800 612 004</a> ·{' '}
        <a href="https://www.rfcswa.com.au/" style={link}>rfcswa.com.au →</a>
      </p>
      <p style={{ ...p, fontSize: '0.88rem', color: 'var(--ink-mid)' }}>
        RFCS WA is the WA delivery partner for the federally-funded Rural
        Financial Counselling Service program. The service is genuinely free
        and entirely confidential — no information goes to banks, ATO or
        Centrelink without your consent.
      </p>

      <h2 style={h2}>Mental health for farming families</h2>
      <p style={p}>
        Drought, fire, frost, market shocks and isolation take a toll. These
        services are built for rural Australians.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li>
          <strong>Rurallink</strong> —{' '}
          <a href="tel:1800552002" style={link}>1800 552 002</a> — after-hours
          mental health phone support for rural WA (4:30pm–8:30am Mon–Fri,
          24/7 weekends and public holidays).
        </li>
        <li>
          <strong>NewAccess for Small Business Owners</strong> (Beyond Blue) —{' '}
          <a href="tel:1300945301" style={link}>1300 945 301</a> — six free
          confidential sessions, no GP referral needed.
        </li>
        <li>
          <strong>Lifeline</strong> —{' '}
          <a href="tel:131114" style={link}>13 11 14</a> — 24/7 crisis support.
        </li>
      </ul>

      <h2 style={h2}>When the MP's office can help</h2>
      <p style={p}>
        Most farming issues are best resolved through DPIRD, your industry
        body or RFCS WA. The electorate office can help with state-government
        interactions that have stalled, advocacy on policy gaps, and
        disaster-event support — drought, frost, fire and storm declarations
        often involve state-funded recovery programs.
      </p>
      <p style={p}>
        See the <Link to="/contact" style={link}>contact page</Link> for
        office hours and the best way to reach the team. Funding programs
        and grants relevant to agriculture are listed on the{' '}
        <Link to="/services/grants" hash="agriculture" style={link}>
          grants finder (agriculture section)
        </Link>
        .
      </p>

      <h2 style={h2}>Trusted sources</h2>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li><a href="https://www.dpird.wa.gov.au/" style={link}>dpird.wa.gov.au</a> — DPIRD home.</li>
        <li><a href="https://www.outbreak.gov.au/" style={link}>outbreak.gov.au</a> — current animal &amp; plant biosecurity alerts.</li>
        <li><a href="https://www.nlis.com.au/" style={link}>nlis.com.au</a> — National Livestock Identification System.</li>
        <li><a href="https://www.integritysystems.com.au/" style={link}>integritysystems.com.au</a> — LPA, eNVD and livestock integrity.</li>
        <li><a href="https://www.rfcswa.com.au/" style={link}>rfcswa.com.au</a> — Rural Financial Counselling.</li>
      </ul>

      <p
        style={{
          marginTop: 'var(--sp-lg)',
          fontSize: '0.78rem',
          color: 'var(--ink-light)',
          borderTop: '1px solid var(--rule)',
          paddingTop: 'var(--sp-sm)',
          lineHeight: 1.5,
        }}
      >
        Community summary of publicly-available information from DPIRD,
        Animal Health Australia, Plant Health Australia, CBH and RFCS WA.
        Programs and contacts change — confirm details on the official sites
        above before acting. Spot something out of date?{' '}
        <a
          href="mailto:bevan.eatts@mp.wa.gov.au?subject=Warren-Blackwood%20guide%20%E2%80%94%20agriculture%20page%20update"
          style={{ color: 'var(--green-mid)', fontWeight: 500 }}
        >
          Email the office
        </a>
        . Last reviewed: June 2026.
      </p>
    </main>
  );
}
